import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PreloaderComponent } from '@shared/components/preloader/preloader.component';
import { filter, map } from 'rxjs';
import { DefaultLayoutComponent } from './layouts/default-layout/default-layout.component';
import { EmptyLayoutComponent } from './layouts/empty-layout/empty-layout.component';

type LayoutType = 'empty' | 'default' | undefined;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'fc-layout',
  templateUrl: './layout.component.html',
  standalone: true,
  imports: [DefaultLayoutComponent, EmptyLayoutComponent, PreloaderComponent],
})
export class LayoutComponent {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  readonly layout = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.getLayoutFromRoute()),
    ),
    { initialValue: this.getLayoutFromRoute() },
  );

  private getLayoutFromRoute(): LayoutType {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout') as LayoutType;
    if (layoutFromQueryParam) {
      return this.normalizeLayout(layoutFromQueryParam);
    }

    for (const path of route.pathFromRoot) {
      const dataLayout = path.routeConfig?.data?.['layout'] as LayoutType;
      if (dataLayout) {
        return this.normalizeLayout(dataLayout);
      }
    }

    return undefined;
  }

  private normalizeLayout(layout: string): LayoutType {
    return layout === 'empty' || layout === 'default' ? layout : undefined;
  }
}
